import {assertAndContinue} from 'Asserts';
import notifications from 'notifications';
import runModal from 'ui-library/runModal';
import CopyTextModal from 'util/clipboard/CopyTextModal';

function notifyCopied(text: string | JSX.Element): void {
  notifications.success(text);
}

function defaultNotifyCopied(text: string): void {
  const maxLen = 100;

  if (text.length > maxLen) {
    text = `${text.substr(0, maxLen)}...`;
  }
  notifyCopied(text);
}

interface CopyToClipboardOptions {
  /** Whether to show a toast upon a successful copy.
   * @default true
   */
  showToastOnSuccess?: boolean;
  /**
   * Text to show in toast
   */
  toastText?: string | JSX.Element;
  /**
   * Function to call upon a successful copy.
   * @default noop
   */
  onSuccess?: () => void;
}

/**
 * Uses the Clipboard API to copy the input text to the clipboard.
 *
 * To test usage, use `getCopyToClipboardSpy()` from test/lib/getCopyToClipboardSpy or stub copyToClipboard.
 *
 * If the copy call fails, a fallback CopyTextModal will open, prompting the user to manually copy
 * the input text.
 *
 * @note copyToClipboard will error (leading to the fallback modal) if the window does not have focus.
 * For example, this will happen if the user triggers an async function ending in copyToClipboard, but
 * alt-tabs away before copyToClipboard is called.
 */
export default function copyToClipboard(text: string, options?: CopyToClipboardOptions): Promise<void> {
  function onCopy(): void {
    if (options?.showToastOnSuccess) {
      if (options?.toastText) {
        notifyCopied(options.toastText);
      } else {
        defaultNotifyCopied(text);
      }
    }
    if (options?.onSuccess) {
      options.onSuccess();
    }
  }

  return navigator.clipboard
    .writeText(text)
    .then(onCopy)
    .catch((reason) => {
      assertAndContinue(false, 'BNCH-19567: Error copying text', {reason, text: text.substr(0, 100)});
      runModal(CopyTextModal, {
        text,
        onCopy,
        onRequestClose: () => {},
      });
    });
}
